import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/BookingForm.jsx. The
// `start_at`/`end_at` conflict check (BookingController::assertNoConflict())
// surfaces as a normal 422 validation error keyed on `start_at`, which
// falls through the existing error-display path with no special handling
// needed.
const toDateTimeLocal = (s: string | null | undefined) => (s ? s.slice(0, 16).replace(' ', 'T') : '');

interface BookingFormState {
  facility_id: string | number;
  title: string;
  booked_by: string;
  start_at: string;
  end_at: string;
  status: string;
  notes: string;
}

const emptyForm: BookingFormState = {
  facility_id: '',
  title: '',
  booked_by: '',
  start_at: '',
  end_at: '',
  status: 'confirmed',
  notes: '',
};

interface FacilityOption {
  id: number;
  name: string;
}

interface BookingRecord {
  facility_id: number | null;
  title: string;
  booked_by: string | null;
  start_at: string;
  end_at: string;
  status: string;
  notes: string | null;
}

type FormErrors = Record<string, string[]>;

export function BookingForm() {
  const { lists } = useConfig();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [form, setForm] = useState<BookingFormState>({
    ...emptyForm,
    facility_id: searchParams.get('facility_id') ?? '',
  });
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ data: FacilityOption[] }>('/facilities', { params: { per_page: 100 } }).then((res) => setFacilities(res.data.data));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get<BookingRecord>(`/bookings/${id}`).then((res) => {
      setForm({
        facility_id: res.data.facility_id ?? '',
        title: res.data.title ?? '',
        booked_by: res.data.booked_by ?? '',
        start_at: toDateTimeLocal(res.data.start_at),
        end_at: toDateTimeLocal(res.data.end_at),
        status: res.data.status ?? 'confirmed',
        notes: res.data.notes ?? '',
      });
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof BookingFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      if (isNew) {
        const res = await api.post<{ id: number }>('/bookings', payload);
        navigate(`/resources/bookings/${res.data.id}`);
      } else {
        await api.put(`/bookings/${id}`, payload);
        navigate(`/resources/bookings/${id}`);
      }
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add booking' : 'Edit booking'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label className="span-2">
            Facility *
            <select value={form.facility_id} onChange={set('facility_id')} required>
              <option value="">Select…</option>
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            {errors.facility_id && <small className="error">{errors.facility_id[0]}</small>}
          </label>
          <label className="span-2">
            Title *
            <input value={form.title} onChange={set('title')} required placeholder="e.g. Committee Meeting" />
            {errors.title && <small className="error">{errors.title[0]}</small>}
          </label>
          <label className="span-2">
            Booked by
            <input value={form.booked_by} onChange={set('booked_by')} placeholder="Name of person or group" />
            {errors.booked_by && <small className="error">{errors.booked_by[0]}</small>}
          </label>
          <label>
            Start *
            <input type="datetime-local" value={form.start_at} onChange={set('start_at')} required />
            {errors.start_at && <small className="error">{errors.start_at[0]}</small>}
          </label>
          <label>
            End *
            <input type="datetime-local" value={form.end_at} onChange={set('end_at')} required />
            {errors.end_at && <small className="error">{errors.end_at[0]}</small>}
          </label>
          <label>
            Status
            <select value={form.status} onChange={set('status')}>
              {listOptions(lists, 'booking_status').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/resources/bookings">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
